import { FC, useCallback, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router';

import { authSelector } from 'store/authState';
import { localizationSelector } from 'store/localizationState';
import { useGetRegisteredProductDetailsQuery } from 'store/productService';

import { useTranslations } from 'hooks/useTranslations';
import { useAppSelector } from 'hooks/redux';
import { pushGTMEvent } from 'utils/analyticsUtils';
import { mavenoidMainProductId } from 'utils/constants';
import * as routes from 'router/routes';
import * as config from 'config';

import SeoHelmet from 'components/fragments/SeoHelmet';
import Chatbot from 'components/fragments/Chatbot';
import ExpandableSection from 'components/UI/ExpandableSection';
import Loader from 'components/UI/Loader';
import Heading from 'components/UI/Heading';
import { IconType } from 'components/UI/Icon';
import PageState from 'components/UI/Page/PageState';
import ProductDealer from 'components/UI/Product/ProductDealer';
import { ProductDocument } from 'components/UI/Product/ProductDocument';
import { ProductDescription } from 'components/UI/Product/ProductDescription';
import ProductImage from 'components/UI/Product/ProductImage';
import ProductInformation from 'components/UI/Product/ProductInformation';
import ProductServiceBook from 'components/UI/Product/ProductServiceBook';
import ProductServices from 'components/UI/Product/ProductServices';
import PageWrapper from 'components/UI/Page/PageWrapper';
import BackButton from 'components/UI/Buttons/BackButton';
import Button from 'components/UI/Buttons/Button';
import DeleteProductModal from 'components/modals/DeleteProductModal';
import DropdownMenu from 'components/UI/DropdownMenu';
import Copyright from 'components/UI/Copyright';

import {
  Container,
  DetailsContainer,
  PageContainer,
  ProductHeader,
  ProductOffer,
  BackgroundContainer,
  Type,
  HeaderContainer
} from './styled';

const ProductDetailsPage: FC = () => {
  const t = useTranslations();
  const { id } = useParams();
  const { session } = useAppSelector(authSelector);
  const { languageCode } = useAppSelector(localizationSelector);

  // State
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

  // Hooks
  const { data } = useGetRegisteredProductDetailsQuery(
    {
      languageCode,
      iprId: id
    },
    { skip: !session?.authenticated }
  );

  // Menu
  const menu = useMemo(
    () => [
      {
        id: 1,
        text: t('productdetails.delete_product'),
        onClick: () => setModalIsOpen(true)
      }
    ],
    [t]
  );

  // Handle modal close
  const onModalClose = useCallback(() => setModalIsOpen(false), []);

  // Redirect to offer
  const onOfferClick = useCallback(() => {
    pushGTMEvent('productOfferClick', {
      productId: data?.product.iprId
    });
    window.open(
      `${config.keylightUrl}/checkout?iprId=${data?.product.iprId}&orderKey=${config.keylightOrderKey}`
    );
  }, [data]);

  // Show loader while fetching data
  if (!data) {
    return (
      <PageWrapper>
        <Loader center size="large" />
      </PageWrapper>
    );
  }

  // Product is not registered or could not get product properly
  if (data && !data.product) {
    return <Navigate to={routes.PRODUCTS} />;
  }

  const { product, dealer, documents, serviceContracts, hasOffers, warranty } =
    data;
  const { iprId, description, productCategory, imageUrl, modelName } = product;

  return (
    <BackgroundContainer>
      <Copyright>
        <SeoHelmet title="productdetails.meta_title" />
        <PageContainer>
          <HeaderContainer>
            <BackButton backUrl={routes.PRODUCTS}>
              {t('productinfo.btn_back')}
            </BackButton>
            <DropdownMenu menu={menu}>
              <Button
                size="small"
                color="transparent"
                textColor="primary"
                border
              >
                {t('productdetails.edit')}
              </Button>
            </DropdownMenu>
          </HeaderContainer>
          <ProductImage src={imageUrl} />
          <Container>
            <ProductHeader>
              <Type>{productCategory?.toLowerCase() ?? '—'}</Type>
              <Heading>{modelName ?? '—'}</Heading>
            </ProductHeader>
            <ProductDescription>{description}</ProductDescription>
            {hasOffers && (
              <ProductOffer>
                <p>{t('productdetails.offers_text')}</p>
                <Button
                  onClick={onOfferClick}
                  color={config.isConstruction ? 'secondary' : 'success'}
                >
                  {t('productdetails.btn_offers')}
                </Button>
              </ProductOffer>
            )}
            <DetailsContainer>
              <ExpandableSection
                title={t('productdetails.section_product_information')}
                openByDefault
              >
                <ProductInformation product={product} warranty={warranty} />
              </ExpandableSection>
              <ExpandableSection
                title={t('productdetails.section_documents')}
                openByDefault
              >
                {!documents.length ? (
                  <PageState
                    iconType={IconType.Document}
                    title={t('productdetails.documents_empty')}
                    small
                  />
                ) : (
                  documents.map((document) => (
                    <ProductDocument key={document.id} document={document} />
                  ))
                )}
              </ExpandableSection>
              <ExpandableSection title={t('productdetails.section_services')}>
                <ProductServices
                  serviceContracts={serviceContracts}
                  languageCode={languageCode}
                />
              </ExpandableSection>
              {dealer && (
                <ExpandableSection
                  title={t('productdetails.section_dealer_information')}
                >
                  <ProductDealer dealer={dealer} />
                </ExpandableSection>
              )}
              <ExpandableSection title={t('productdetails.section_dsb')} isLast>
                <ProductServiceBook data={data.serviceRecords} />
              </ExpandableSection>
            </DetailsContainer>
          </Container>
        </PageContainer>
        <DeleteProductModal
          isOpen={modalIsOpen}
          close={onModalClose}
          iprId={iprId}
          customerId={session.user?.attributes.customerId || ''}
        />
        <Chatbot productId={mavenoidMainProductId} langCode={languageCode} />
      </Copyright>
    </BackgroundContainer>
  );
};

export default ProductDetailsPage;
